import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '@/v3-dashboard/components/UI/Modal';
import {
  getFeatureNotAvailableTitlePro,
  getFeatureSubtitleProToEnterprise,
} from '@/helpers/tier/featureTitles';
import { getAddonConfigForFeature, PLAN_AND_ADDON_SUBHEADER } from './addonConfigs';
import AgencyPlanIcon from '@/shared/icons/AgencyPlanIcon';
import Button from '@/shared/components/UI/Button';
import PlanInfoBox from './PlanInfoBox';
import FeaturesList from './FeaturesList';
import AddonSection from './AddonSection';
import mixpanel from '@/utils/mixpanel';
import useModalStore from '@/stores/modal/modalStore';
import useTrackModalViewed from '@/hooks/useTrackModalViewed';
import { getTierFeaturesConfig, FEATURE_KEY_TO_LABEL } from '@/v3-dashboard/pages/Billing/tierFeatures';

const ENTERPRISE_EMAIL = 'hello@dante-ai.com';
const ENTERPRISE_SUBJECT = 'Enterprise Plan Request';

const ProToEnterpriseModal = ({ isOpen, onClose, feature, currentTier }) => {
  const navigate = useNavigate();
  const addonConfig = getAddonConfigForFeature(feature);

  const title = getFeatureNotAvailableTitlePro(feature);
  const subtitle = getFeatureSubtitleProToEnterprise(feature);
  const enterpriseFeatures = getTierFeaturesConfig('enterprise').key_features;
  const highlightLabel = feature ? FEATURE_KEY_TO_LABEL[feature] : null;

  const modalStore = useModalStore();
  const commonTrackingProps = {
    version: 'pricing-modal-v2',
    modal_variant: modalStore.modalVariant,
    modal_type: addonConfig ? 'plan_and_addon' : 'plan_only',
    current_tier: currentTier,
    target_tier: 'enterprise',
    feature_name: feature || null,
    feature_title: null,
    source: modalStore.upgradeContext?.source || null,
    addon_shown: addonConfig?.addonKey || null,
    addon_label: addonConfig?.label || null,
    plan_shown: 'enterprise',
    plan_monthly_price: null,
    plan_yearly_price: null,
  };

  useTrackModalViewed(isOpen, modalStore.upgradeContext, commonTrackingProps);

  const handleDismiss = (method = 'close_button') => {
    mixpanel.track('Upgrade Path Modal Dismissed', {
      ...commonTrackingProps,
      dismiss_method: method,
    });
    onClose();
  };

  const handleSeeAllPlans = () => {
    mixpanel.track('Upgrade Path Modal See All Plans Clicked', commonTrackingProps);
    onClose();
    navigate('/plans');
  };

  const handleContactEnterprise = () => {
    mixpanel.track('Upgrade Path Enterprise Contact Clicked', commonTrackingProps);
    onClose();
    window.location.href = `mailto:${ENTERPRISE_EMAIL}?subject=${encodeURIComponent(ENTERPRISE_SUBJECT)}`;
  };

  const planUpgradeContent = (
    <>
      <PlanInfoBox
        icon={<AgencyPlanIcon className="text-v3-orange-primary" />}
        title="Enterprise plan"
        subtitle="For teams that need custom scale, limits, and support"
      />
      <FeaturesList
        features={enterpriseFeatures}
        maxFeatures={5}
        highlightFeature={highlightLabel}
      />
      <span className="font-inter-variable text-xs font-medium leading-4 text-v3-black-secondary">
        Custom pricing. Click the button below to speak to us.
      </span>
    </>
  );

  const planUpgradeButton = (
    <Button
      variant="primary"
      size="md"
      label="Let&apos;s Talk"
      onClick={handleContactEnterprise}
      className="w-full justify-center"
    />
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleDismiss}
      title={title}
      subtitle={addonConfig ? PLAN_AND_ADDON_SUBHEADER : subtitle}
      closeBackdrop
      className={addonConfig ? 'md:!w-[720px]' : 'md:!w-[550px]'}
    >
      {addonConfig ? (
        <div className="flex items-stretch w-full min-h-[320px]">
          <div className="flex flex-col items-start flex-1 min-w-0">
            <div className="flex flex-col items-start w-full gap-v3-lg">
              {planUpgradeContent}
            </div>
            <div className="flex flex-col items-stretch w-full mt-auto pt-v3-lg">
              {planUpgradeButton}
            </div>
          </div>
          <div className="flex flex-col items-center justify-center shrink-0 px-v3-md">
            <div className="flex-1 min-h-[24px] w-px bg-v3-black-border" />
            <span className="font-lora italic text-v3-h10 text-v3-black-secondary py-v3-xs">OR</span>
            <div className="flex-1 min-h-[24px] w-px bg-v3-black-border" />
          </div>
          <AddonSection addonConfig={addonConfig} />
        </div>
      ) : (
        <div className="flex flex-col items-start w-full gap-v3-lg">
          {planUpgradeContent}
          {planUpgradeButton}
        </div>
      )}

      <div className="flex flex-col items-stretch gap-v3-xs w-full mt-v3-md">
        <Button
          variant="transparent"
          size="md"
          label="See all plans"
          onClick={handleSeeAllPlans}
          className="w-full justify-center"
        />
      </div>
    </Modal>
  );
};

export default ProToEnterpriseModal;
